"use client";

import * as React from "react";
import { Box, Wrap, Badge, Spinner } from "@chakra-ui/react";
import { Combobox, useListCollection, useFilter } from "@chakra-ui/react";

export interface UniversalSelectItem {
  label: string;
  value: string;
}

export interface SelectPositioning {
  placement?:
    | "top"
    | "top-start"
    | "top-end"
    | "bottom"
    | "bottom-start"
    | "bottom-end"
    | "left"
    | "left-start"
    | "left-end"
    | "right"
    | "right-start"
    | "right-end";
  strategy?: "absolute" | "fixed";
  flip?: boolean;
  sameWidth?: boolean;
  gutter?: number;
}

export interface UniversalSelectProps {
  defaultValue?: string | string[];
  debounceMs?: number;
  disabled?: boolean;
  error?: string;
  hasMore?: boolean;
  id: string;
  items?: UniversalSelectItem[];
  label: string;
  loading?: boolean;
  loadingMore?: boolean;
  minSearchChars?: number;
  multiple?: boolean;
  onChange?: (value: string | string[]) => void;
  onLoadMore?: () => void;
  onSearch?: (search: string) => void;
  placeholder?: string;
  positioning?: SelectPositioning;
  scrollBehavior?: string; // "inside" => dropdown scroll
  searchable?: boolean;
  value?: string | string[];
}

const toArray = (v?: string | string[]): string[] => {
  if (Array.isArray(v)) return v;
  if (typeof v === "string" && v.length > 0) return [v];
  return [];
};

export const UniversalSelect: React.FC<UniversalSelectProps> = (props) => {
  const {
    debounceMs = 300,
    disabled = false,
    error,
    hasMore = false,
    id,
    items = [],
    label,
    loading = false,
    loadingMore = false,
    minSearchChars = 3,
    multiple = false,
    onLoadMore,
    onSearch,
    placeholder = "Select...",
    positioning = {
      flip: false,
      placement: "bottom-start",
      sameWidth: true,
      strategy: "fixed",
    },
    scrollBehavior = "inside",
    searchable = true,
    value: externalValue,
    defaultValue,
  } = props;

  const isInsideScroll = scrollBehavior === "inside";

  // ---------- controlled / uncontrolled selected values ----------
  const isControlled = externalValue !== undefined;

  const [internalValues, setInternalValues] = React.useState<string[]>(() =>
    defaultValue ? toArray(defaultValue) : [],
  );

  const selectedValues = isControlled ? toArray(externalValue) : internalValues;

  const updateSelectedValues = React.useCallback(
    (next: string[]) => {
      if (!isControlled) {
        setInternalValues(next);
      }

      if (props.onChange) {
        if (multiple) props.onChange(next);
        else props.onChange(next[0] ?? "");
      }
    },
    [isControlled, multiple, props],
  );

  // ---------- Combobox open state (yahi se dropdown close karenge) ----------
  const [isOpen, setIsOpen] = React.useState(false);

  // ---------- search + collection handling ----------
  const { contains } = useFilter({ sensitivity: "base" });

  const { collection, set } = useListCollection<UniversalSelectItem>({
    initialItems: items,
    itemToString: (item) => item.label,
    itemToValue: (item) => item.value,
  });

  const itemsRef = React.useRef<UniversalSelectItem[]>(items);
  const lastQueryRef = React.useRef<string>("");
  const searchTimeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    itemsRef.current = items;

    const q = lastQueryRef.current.trim();

    if (!searchable) {
      set(items);
      return;
    }

    if (!q) {
      set(items);
      return;
    }

    const filtered = items.filter((it) => contains(it.label, q));
    set(filtered);
  }, [items, searchable, set]);

  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current !== null) {
        window.clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleInputValueChange = React.useCallback(
    (details: Combobox.InputValueChangeDetails) => {
      const raw = details.inputValue ?? "";
      const query = raw.trim();

      lastQueryRef.current = raw;

      if (!searchable) return;

      if (!query) {
        set(itemsRef.current);
        if (selectedValues.length > 0) {
          updateSelectedValues([]);
        }
        return;
      }

      const locallyFiltered = itemsRef.current.filter((it) =>
        contains(it.label, query),
      );
      set(locallyFiltered);

      if (
        onSearch &&
        locallyFiltered.length === 0 &&
        query.length >= minSearchChars
      ) {
        if (searchTimeoutRef.current !== null) {
          window.clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = window.setTimeout(() => {
          onSearch(query);
        }, debounceMs);
      }
    },
    [
      searchable,
      onSearch,
      minSearchChars,
      debounceMs,
      set,
      contains,
      updateSelectedValues,
      selectedValues,
    ],
  );

  const applySelection = React.useCallback(
    (next: string[], closeDropdown: boolean) => {
      updateSelectedValues(next);
      if (closeDropdown) {
        setIsOpen(false);
      }
    },
    [updateSelectedValues],
  );

  const handleValueChange = React.useCallback(
    (details: Combobox.ValueChangeDetails) => {
      const raw = details.value;
      const arr = Array.isArray(raw) ? raw : [raw];
      applySelection(arr, !multiple);
    },
    [applySelection, multiple],
  );

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;

    const visibleItems = collection.items as UniversalSelectItem[];

    if (visibleItems.length !== 1) return;

    const single = visibleItems[0];
    let next: string[];

    if (multiple) {
      const exists = selectedValues.includes(single.value);
      next = exists ? selectedValues : [...selectedValues, single.value];
    } else {
      next = [single.value];
    }

    e.preventDefault();
    applySelection(next, !multiple);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore || !onLoadMore || loadingMore) return;
    const target = e.currentTarget;
    const threshold = 24;

    if (
      target.scrollTop + target.clientHeight >=
      target.scrollHeight - threshold
    ) {
      onLoadMore();
    }
  };

  const selectedBadges =
    multiple && selectedValues.length > 0 ? (
      <Wrap mb={2}>
        {selectedValues.map((val) => {
          const item = itemsRef.current.find((i) => i.value === val);
          return (
            <Badge key={val} colorScheme="blue">
              {item?.label ?? val}
            </Badge>
          );
        })}
      </Wrap>
    ) : null;

  return (
    <Box>
      <Combobox.Root
        multiple={multiple}
        collection={collection}
        value={selectedValues}
        onValueChange={handleValueChange}
        disabled={disabled}
        closeOnSelect={!multiple}
        width="100%"
        name={id}
        positioning={positioning}
        openOnClick
        open={isOpen}
        onOpenChange={(details) => setIsOpen(details.open)}
        onInputValueChange={handleInputValueChange}
      >
        {label && <Combobox.Label>{label}</Combobox.Label>}
        {selectedBadges}

        <Combobox.Control display="flex" alignItems="center" w="100%">
          <Combobox.Input
            placeholder={placeholder}
            flex="1"
            pr="14"
            onKeyDown={handleInputKeyDown}
          />
          <Combobox.IndicatorGroup display="flex" alignItems="center" gap={1}>
            {loading && <Spinner size="xs" />}
            <Combobox.ClearTrigger />
            <Combobox.Trigger />
          </Combobox.IndicatorGroup>
        </Combobox.Control>

        <Combobox.Positioner>
          <Combobox.Content
            maxH={isInsideScroll ? "240px" : undefined}
            overflowY={isInsideScroll ? "auto" : undefined}
            onScroll={isInsideScroll ? handleScroll : undefined}
          >
            <Combobox.Empty>No items found</Combobox.Empty>

            {collection.items.map((item: any, idx: number) => (
              <Combobox.Item key={`${item.value}__${idx}`} item={item}>
                {item.label}
                <Combobox.ItemIndicator />
              </Combobox.Item>
            ))}

            {loadingMore && (
              <Box py={2} textAlign="center">
                <Spinner size="xs" />
              </Box>
            )}
          </Combobox.Content>
        </Combobox.Positioner>

        {error && (
          <Box mt={1} fontSize="sm" color="red.500">
            {error}
          </Box>
        )}
      </Combobox.Root>
    </Box>
  );
};

export default UniversalSelect;
